import type { Graphics } from 'pixi.js';

export type PetType = 'dog' | 'turtle' | 'guinea_pig' | 'parrot' | 'cat' | 'panda'
  | 'fox' | 'squirrel' | 'dragon' | 'owl' | 'rabbit'
  | 'goldfish' | 'hamster' | 'snake' | 'frog' | 'mouse'
  | 'shark' | 'rat';

export const PET_NAMES: Record<PetType, string> = {
  dog:        'Hund',
  turtle:     'Sköldpadda',
  guinea_pig: 'Marsvin',
  parrot:     'Papegoja',
  cat:        'Katt',
  panda:      'Panda',
  fox:        'Räv',
  squirrel:   'Ekorre',
  dragon:     'Drake',
  owl:        'Uggla',
  rabbit:     'Dumisens kanin',
  goldfish:   'Guldfisk',
  hamster:    'Hamster',
  snake:      'Orm',
  frog:       'Groda',
  mouse:      'Mus',
  shark:      'Haj',
  rat:        'Råtta',
};

export const PET_CODES: Partial<Record<PetType, string>> = {
  cat:      '16',
  panda:    '666',
  fox:      '44',
  squirrel: '12',
  dragon:   '300',
  owl:      '93',
  rabbit:   'dumisen',
  rat:      'Levi B',
};

/** Cat colour variants (selectable in lobby when cat is unlocked). */
export type CatColor = 'gray' | 'orange' | 'black' | 'tuxedo';

/** Draw pet at (x, y) — top-down view, facing right.
 *  `variant` is currently only used for cats. */
export function drawPet(g: Graphics, pet: PetType | null, x: number, y: number, variant?: string): void {
  if (!pet) return;
  switch (pet) {
    case 'dog':        drawDog(g, x, y);       break;
    case 'turtle':     drawTurtle(g, x, y);    break;
    case 'guinea_pig': drawGuineaPig(g, x, y); break;
    case 'parrot':     drawParrot(g, x, y);    break;
    case 'cat':        drawCat(g, x, y, (variant as CatColor) || 'gray'); break;
    case 'panda':      drawPanda(g, x, y);     break;
    case 'fox':        drawFox(g, x, y);       break;
    case 'squirrel':   drawSquirrel(g, x, y);  break;
    case 'dragon':     drawDragon(g, x, y);    break;
    case 'owl':        drawOwl(g, x, y);       break;
    case 'rabbit':     drawRabbit(g, x, y);    break;
    case 'goldfish':   drawGoldfish(g, x, y);  break;
    case 'hamster':    drawHamster(g, x, y);   break;
    case 'snake':      drawSnake(g, x, y);     break;
    case 'frog':       drawFrog(g, x, y);      break;
    case 'mouse':      drawMouse(g, x, y);     break;
    case 'shark':      drawShark(g, x, y);     break;
    case 'rat':        drawRat(g, x, y);       break;
  }
}

// ---- Dog ----
function drawDog(g: Graphics, x: number, y: number): void {
  // Shadow
  g.ellipse(x + 1, y + 1, 6, 4.5);
  g.fill({ color: 0x000000, alpha: 0.12 });
  // Body
  g.ellipse(x, y, 6, 4.5);
  g.fill({ color: 0xc48040 });
  g.ellipse(x, y, 6, 4.5);
  g.stroke({ color: 0x8a5020, width: 0.8 });
  // Tail (behind, small nub)
  g.ellipse(x - 5.5, y + 1, 2.5, 1.5);
  g.fill({ color: 0xb07030 });
  // Head
  g.circle(x + 7, y, 3.5);
  g.fill({ color: 0xd49050 });
  g.circle(x + 7, y, 3.5);
  g.stroke({ color: 0x8a5020, width: 0.8 });
  // Floppy ears
  g.ellipse(x + 6, y - 4, 2.2, 3.5);
  g.fill({ color: 0xa06030 });
  g.ellipse(x + 6, y + 4, 2.2, 3.5);
  g.fill({ color: 0xa06030 });
  // Nose
  g.circle(x + 10, y, 1.2);
  g.fill({ color: 0x222222 });
  // Eye
  g.circle(x + 8, y - 1.5, 0.8);
  g.fill({ color: 0x111111 });
}

// ---- Turtle ----
function drawTurtle(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 1, y + 1, 6, 5);
  g.fill({ color: 0x000000, alpha: 0.12 });
  // Shell
  g.ellipse(x, y, 6, 5);
  g.fill({ color: 0x6a8a30 });
  g.ellipse(x, y, 6, 5);
  g.stroke({ color: 0x3a5a10, width: 1 });
  // Shell pattern (segments)
  g.moveTo(x, y - 4); g.lineTo(x, y + 4);
  g.stroke({ color: 0x3a5a10, width: 0.8 });
  g.moveTo(x - 4, y); g.lineTo(x + 4, y);
  g.stroke({ color: 0x3a5a10, width: 0.8 });
  g.moveTo(x - 3, y - 2.5); g.lineTo(x + 3, y + 2.5);
  g.stroke({ color: 0x3a5a10, width: 0.5 });
  g.moveTo(x + 3, y - 2.5); g.lineTo(x - 3, y + 2.5);
  g.stroke({ color: 0x3a5a10, width: 0.5 });
  // Head sticking out front
  g.circle(x + 7.5, y, 2.8);
  g.fill({ color: 0x4a7020 });
  g.circle(x + 7.5, y, 2.8);
  g.stroke({ color: 0x2a4a10, width: 0.7 });
  // Eye
  g.circle(x + 8.5, y - 1, 0.8);
  g.fill({ color: 0x111111 });
  // Small feet
  g.ellipse(x - 3, y - 5, 2, 1.2); g.fill({ color: 0x4a7020 });
  g.ellipse(x - 3, y + 5, 2, 1.2); g.fill({ color: 0x4a7020 });
  g.ellipse(x + 3, y - 5, 2, 1.2); g.fill({ color: 0x4a7020 });
  g.ellipse(x + 3, y + 5, 2, 1.2); g.fill({ color: 0x4a7020 });
}

// ---- Guinea Pig ----
function drawGuineaPig(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 0.8, y + 1, 6, 5.5);
  g.fill({ color: 0x000000, alpha: 0.10 });
  // Fluffy body
  g.ellipse(x, y, 6, 5.5);
  g.fill({ color: 0xe8d090 });
  g.ellipse(x, y, 6, 5.5);
  g.stroke({ color: 0xc0a050, width: 0.8 });
  // Color patches
  g.ellipse(x + 1, y - 1, 3, 2.5);
  g.fill({ color: 0xc06030, alpha: 0.6 });
  // Head (round, slightly different shade)
  g.circle(x + 7, y, 4);
  g.fill({ color: 0xf0d898 });
  g.circle(x + 7, y, 4);
  g.stroke({ color: 0xc0a050, width: 0.8 });
  // Round ears
  g.circle(x + 6, y - 4.5, 2);
  g.circle(x + 6, y + 4.5, 2);
  g.fill({ color: 0xe8a0a0 });
  // Tiny nose
  g.circle(x + 10.5, y, 0.9);
  g.fill({ color: 0xee8888 });
  // Eyes
  g.circle(x + 8.5, y - 1.5, 0.9);
  g.fill({ color: 0x111111 });
  // Shine dot on eye
  g.circle(x + 8.8, y - 1.8, 0.35);
  g.fill({ color: 0xffffff });
}

// ---- Parrot ----
function drawParrot(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 0.8, y + 1, 5.5, 4.5);
  g.fill({ color: 0x000000, alpha: 0.10 });
  // Body (green)
  g.ellipse(x, y, 5.5, 4.5);
  g.fill({ color: 0x30a840 });
  g.ellipse(x, y, 5.5, 4.5);
  g.stroke({ color: 0x107820, width: 0.8 });
  // Wing accent (blue)
  g.ellipse(x - 1, y + 1, 3.5, 2);
  g.fill({ color: 0x3060d0, alpha: 0.7 });
  // Tail feathers (colourful)
  g.moveTo(x - 5, y - 1); g.lineTo(x - 9, y - 3);
  g.stroke({ color: 0xe0c020, width: 2, cap: 'round' });
  g.moveTo(x - 5, y);     g.lineTo(x - 9, y);
  g.stroke({ color: 0xd03030, width: 2, cap: 'round' });
  g.moveTo(x - 5, y + 1); g.lineTo(x - 9, y + 3);
  g.stroke({ color: 0x3080e0, width: 2, cap: 'round' });
  // Head (red)
  g.circle(x + 6.5, y, 3.5);
  g.fill({ color: 0xe03020 });
  g.circle(x + 6.5, y, 3.5);
  g.stroke({ color: 0xa01810, width: 0.8 });
  // Beak (hooked, yellow)
  g.moveTo(x + 9.5, y); g.lineTo(x + 11.5, y + 1.5); g.lineTo(x + 10, y + 2);
  g.closePath();
  g.fill({ color: 0xe0c020 });
  // Eye
  g.circle(x + 7.5, y - 1.5, 1);
  g.fill({ color: 0x111111 });
  g.circle(x + 7.8, y - 1.8, 0.4);
  g.fill({ color: 0xffffff });
}

// ---- Cat ----
const CAT_PALETTES: Record<CatColor, { body: number; head: number; stroke: number; stripe: number; whisker: number }> = {
  gray:   { body: 0xc0b8a8, head: 0xd0c8b8, stroke: 0x807870, stripe: 0x808070, whisker: 0xffffff },
  orange: { body: 0xe08030, head: 0xf09840, stroke: 0xa04810, stripe: 0xc05010, whisker: 0xffe0c0 },
  black:  { body: 0x222222, head: 0x2a2a2a, stroke: 0x000000, stripe: 0x101010, whisker: 0xffffff },
  tuxedo: { body: 0x222222, head: 0x2a2a2a, stroke: 0x000000, stripe: 0x101010, whisker: 0xffffff },
};

function drawCat(g: Graphics, x: number, y: number, variant: CatColor = 'gray'): void {
  const c = CAT_PALETTES[variant];
  g.ellipse(x + 0.8, y + 1, 5.5, 4.5);
  g.fill({ color: 0x000000, alpha: 0.10 });
  // Body
  g.ellipse(x, y, 5.5, 4.5);
  g.fill({ color: c.body });
  g.ellipse(x, y, 5.5, 4.5);
  g.stroke({ color: c.stroke, width: 0.8 });
  // Stripes / spots
  if (variant === 'tuxedo') {
    // Grey patches on black body
    g.ellipse(x - 2, y - 1.5, 1.6, 1);
    g.fill({ color: 0x909090 });
    g.ellipse(x + 1.5, y + 1.5, 1.4, 1);
    g.fill({ color: 0x909090 });
    g.ellipse(x - 1, y + 2.5, 1.2, 0.8);
    g.fill({ color: 0x909090 });
  } else {
    g.moveTo(x - 2, y - 3); g.lineTo(x + 2, y - 3);
    g.moveTo(x - 2, y + 3); g.lineTo(x + 2, y + 3);
    g.stroke({ color: c.stripe, width: 1, alpha: 0.5 });
  }
  // Curved tail
  g.moveTo(x - 5, y);
  g.quadraticCurveTo(x - 8, y - 4, x - 6, y - 7);
  g.stroke({ color: c.body, width: 2, cap: 'round' });
  // Ears (above eyes)
  g.moveTo(x + 4.2, y - 3); g.lineTo(x + 4.8, y - 7); g.lineTo(x + 6.8, y - 3.2);
  g.closePath(); g.fill({ color: c.head }); g.stroke({ color: c.stroke, width: 0.8 });
  g.moveTo(x + 7.2, y - 3.2); g.lineTo(x + 9.2, y - 7); g.lineTo(x + 9.8, y - 3);
  g.closePath(); g.fill({ color: c.head }); g.stroke({ color: c.stroke, width: 0.8 });
  // Inner ear pink
  g.moveTo(x + 4.8, y - 3.2); g.lineTo(x + 5.2, y - 5.9); g.lineTo(x + 6.6, y - 3.4);
  g.closePath(); g.fill({ color: 0xff88aa });
  g.moveTo(x + 7.4, y - 3.4); g.lineTo(x + 8.8, y - 5.9); g.lineTo(x + 9.2, y - 3.2);
  g.closePath(); g.fill({ color: 0xff88aa });
  // Head
  g.circle(x + 7, y, 3.8);
  g.fill({ color: c.head });
  g.circle(x + 7, y, 3.8);
  g.stroke({ color: c.stroke, width: 0.8 });
  // Tuxedo white chin patch
  if (variant === 'tuxedo') {
    g.ellipse(x + 7, y + 2.5, 1.5, 0.8);
    g.fill({ color: 0xf5f5f0 });
  }
  // Almond eyes (green)
  g.ellipse(x + 5.8, y - 0.3, 1.1, 0.9);
  g.fill({ color: 0x50c050 });
  g.ellipse(x + 8.2, y - 0.3, 1.1, 0.9);
  g.fill({ color: 0x50c050 });
  g.circle(x + 5.8, y - 0.3, 0.5);
  g.circle(x + 8.2, y - 0.3, 0.5);
  g.fill({ color: 0x111111 });
  // Nose
  g.moveTo(x + 6.5, y + 1.3); g.lineTo(x + 7, y + 2); g.lineTo(x + 7.5, y + 1.3);
  g.closePath(); g.fill({ color: 0xffaabb });
  // Whiskers
  g.moveTo(x + 6, y + 1.6); g.lineTo(x + 3.5, y + 2.5);
  g.moveTo(x + 8, y + 1.6); g.lineTo(x + 10.5, y + 2.5);
  g.stroke({ color: c.whisker, width: 0.6, alpha: 0.8 });
}

// ---- Panda ----
function drawPanda(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 1, y + 1.5, 7, 6);
  g.fill({ color: 0x000000, alpha: 0.12 });
  // Body (white)
  g.ellipse(x, y, 7, 6);
  g.fill({ color: 0xf0f0f0 });
  g.ellipse(x, y, 7, 6);
  g.stroke({ color: 0x888888, width: 0.8 });
  // Black shoulder patches
  g.ellipse(x - 2, y - 4, 4, 2.5);
  g.ellipse(x - 2, y + 4, 4, 2.5);
  g.fill({ color: 0x111111 });
  // Head (white, large)
  g.circle(x + 8, y, 5);
  g.fill({ color: 0xf0f0f0 });
  g.circle(x + 8, y, 5);
  g.stroke({ color: 0x888888, width: 0.8 });
  // Black ears
  g.circle(x + 6.5, y - 5.5, 2.5);
  g.circle(x + 6.5, y + 5.5, 2.5);
  g.fill({ color: 0x111111 });
  // Black eye patches
  g.ellipse(x + 8.5, y - 2, 2, 1.8);
  g.ellipse(x + 8.5, y + 2, 2, 1.8);
  g.fill({ color: 0x111111 });
  // White eyes inside patches
  g.circle(x + 8.8, y - 2, 0.9);
  g.circle(x + 8.8, y + 2, 0.9);
  g.fill({ color: 0xffffff });
  // Pupils
  g.circle(x + 8.8, y - 2, 0.5);
  g.circle(x + 8.8, y + 2, 0.5);
  g.fill({ color: 0x111111 });
  // Nose
  g.ellipse(x + 12.5, y, 1.5, 1.2);
  g.fill({ color: 0x333333 });
}

// ---- Fox (code 44) ----
function drawFox(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 0.8, y + 1, 5.5, 4);
  g.fill({ color: 0x000000, alpha: 0.12 });
  // Body (orange)
  g.ellipse(x, y, 5.5, 4);
  g.fill({ color: 0xe07030 });
  g.ellipse(x, y, 5.5, 4);
  g.stroke({ color: 0xa0401a, width: 0.8 });
  // Bushy white-tipped tail
  g.ellipse(x - 6, y, 3, 2);
  g.fill({ color: 0xe07030 });
  g.ellipse(x - 8, y, 1.5, 1.3);
  g.fill({ color: 0xffffff });
  // White belly streak
  g.ellipse(x, y, 3, 1.5);
  g.fill({ color: 0xfff0e0, alpha: 0.85 });
  // Head (slightly lighter, pointed snout)
  g.circle(x + 6, y, 3.2);
  g.fill({ color: 0xe88040 });
  g.circle(x + 6, y, 3.2);
  g.stroke({ color: 0xa0401a, width: 0.8 });
  // Pointy ears
  g.moveTo(x + 4, y - 3); g.lineTo(x + 4.5, y - 6); g.lineTo(x + 6.5, y - 3);
  g.closePath(); g.fill({ color: 0xc05020 });
  g.moveTo(x + 5.5, y + 3); g.lineTo(x + 7.5, y + 6); g.lineTo(x + 8, y + 3);
  g.closePath(); g.fill({ color: 0xc05020 });
  // Pointed snout (white tip)
  g.moveTo(x + 8.5, y - 1); g.lineTo(x + 11, y); g.lineTo(x + 8.5, y + 1);
  g.closePath(); g.fill({ color: 0xfff0e0 });
  // Nose
  g.circle(x + 10.8, y, 0.7);
  g.fill({ color: 0x111111 });
  // Eye
  g.circle(x + 7, y - 1.5, 0.8);
  g.fill({ color: 0x111111 });
}

// ---- Squirrel (code 12) ----
function drawSquirrel(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 0.8, y + 1, 4.5, 3.5);
  g.fill({ color: 0x000000, alpha: 0.10 });
  // Body (grey-brown)
  g.ellipse(x, y, 4.5, 3.5);
  g.fill({ color: 0x9a7050 });
  g.ellipse(x, y, 4.5, 3.5);
  g.stroke({ color: 0x5a3a20, width: 0.7 });
  // Huge bushy tail curled up
  g.ellipse(x - 4, y - 4, 3.5, 5);
  g.fill({ color: 0xa88060 });
  g.ellipse(x - 4, y - 4, 3.5, 5);
  g.stroke({ color: 0x5a3a20, width: 0.6 });
  // Light belly
  g.ellipse(x + 1, y, 2.5, 1.8);
  g.fill({ color: 0xe8d0a0, alpha: 0.8 });
  // Head
  g.circle(x + 5, y, 2.8);
  g.fill({ color: 0xa88060 });
  g.circle(x + 5, y, 2.8);
  g.stroke({ color: 0x5a3a20, width: 0.7 });
  // Small tufted ears
  g.ellipse(x + 4, y - 3, 1, 1.4);
  g.fill({ color: 0x7a5020 });
  g.ellipse(x + 4, y + 3, 1, 1.4);
  g.fill({ color: 0x7a5020 });
  // Tiny nose + eye
  g.circle(x + 7.5, y, 0.7);
  g.fill({ color: 0x111111 });
  g.circle(x + 6, y - 1.3, 0.7);
  g.fill({ color: 0x111111 });
  // Nut (acorn) held in front
  g.ellipse(x + 8.5, y + 1.5, 1.3, 1);
  g.fill({ color: 0x805020 });
}

// ---- Dragon (code 300) ----
function drawDragon(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 1, y + 1.5, 7, 5);
  g.fill({ color: 0x000000, alpha: 0.15 });
  // Long body (green)
  g.ellipse(x, y, 7, 4.5);
  g.fill({ color: 0x3aa050 });
  g.ellipse(x, y, 7, 4.5);
  g.stroke({ color: 0x1a6020, width: 0.9 });
  // Lighter belly
  g.ellipse(x, y, 5, 2);
  g.fill({ color: 0x90d070, alpha: 0.9 });
  // Wings — spread out
  g.moveTo(x - 1, y - 3.5); g.lineTo(x - 4, y - 8); g.lineTo(x + 2, y - 6);
  g.closePath();
  g.fill({ color: 0x60c070 });
  g.stroke({ color: 0x1a6020, width: 0.7 });
  g.moveTo(x - 1, y + 3.5); g.lineTo(x - 4, y + 8); g.lineTo(x + 2, y + 6);
  g.closePath();
  g.fill({ color: 0x60c070 });
  g.stroke({ color: 0x1a6020, width: 0.7 });
  // Spiky tail
  g.moveTo(x - 7, y); g.lineTo(x - 12, y - 1.5); g.lineTo(x - 10, y);
  g.lineTo(x - 12, y + 1.5); g.closePath();
  g.fill({ color: 0x3aa050 });
  g.stroke({ color: 0x1a6020, width: 0.7 });
  // Head
  g.circle(x + 8, y, 3.8);
  g.fill({ color: 0x3aa050 });
  g.circle(x + 8, y, 3.8);
  g.stroke({ color: 0x1a6020, width: 0.8 });
  // Horns
  g.moveTo(x + 6, y - 3); g.lineTo(x + 6.5, y - 6); g.lineTo(x + 7.5, y - 3);
  g.closePath(); g.fill({ color: 0xc0a060 });
  g.moveTo(x + 8.5, y - 3); g.lineTo(x + 9.5, y - 6); g.lineTo(x + 10, y - 3);
  g.closePath(); g.fill({ color: 0xc0a060 });
  // Fierce yellow eyes
  g.circle(x + 9, y - 1.5, 0.9);
  g.fill({ color: 0xffcc00 });
  g.circle(x + 9, y - 1.5, 0.4);
  g.fill({ color: 0x111111 });
  // Flame puff from nose
  g.circle(x + 12, y, 1);
  g.fill({ color: 0xff4020, alpha: 0.8 });
  g.circle(x + 13.5, y, 0.7);
  g.fill({ color: 0xffaa20, alpha: 0.7 });
}

// ---- Owl (code 93) ----
function drawOwl(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 1, y + 1, 5.5, 5);
  g.fill({ color: 0x000000, alpha: 0.12 });
  // Round brown body
  g.ellipse(x, y, 5.5, 5);
  g.fill({ color: 0x6a4a28 });
  g.ellipse(x, y, 5.5, 5);
  g.stroke({ color: 0x3a2a18, width: 0.8 });
  // Feather chest (lighter)
  g.ellipse(x + 1, y, 3.5, 3.2);
  g.fill({ color: 0xc89a60 });
  // Feather spots
  for (const [dx, dy] of [[-1, -1], [1, 1], [-0.5, 1.5], [1.5, -1.2]]) {
    g.circle(x + dx, y + dy, 0.4);
    g.fill({ color: 0x5a3a18 });
  }
  // Wings
  g.ellipse(x - 1.5, y - 3, 1.8, 2.5);
  g.fill({ color: 0x4a3018 });
  g.ellipse(x - 1.5, y + 3, 1.8, 2.5);
  g.fill({ color: 0x4a3018 });
  // Head disc (same as body, large)
  g.circle(x + 5.5, y, 4);
  g.fill({ color: 0x7a5a38 });
  g.circle(x + 5.5, y, 4);
  g.stroke({ color: 0x3a2a18, width: 0.8 });
  // Ear tufts
  g.moveTo(x + 4, y - 3.5); g.lineTo(x + 4.5, y - 5.5); g.lineTo(x + 5.5, y - 3);
  g.closePath(); g.fill({ color: 0x4a3018 });
  g.moveTo(x + 5.5, y + 3); g.lineTo(x + 6.5, y + 5.5); g.lineTo(x + 7, y + 3.5);
  g.closePath(); g.fill({ color: 0x4a3018 });
  // Big yellow eyes
  g.circle(x + 6, y - 1.5, 1.5);
  g.fill({ color: 0xffd040 });
  g.circle(x + 6, y + 1.5, 1.5);
  g.fill({ color: 0xffd040 });
  g.circle(x + 6.3, y - 1.5, 0.7);
  g.circle(x + 6.3, y + 1.5, 0.7);
  g.fill({ color: 0x111111 });
  // Beak
  g.moveTo(x + 8.5, y - 0.8); g.lineTo(x + 10, y); g.lineTo(x + 8.5, y + 0.8);
  g.closePath(); g.fill({ color: 0xe0a040 });
}

// ---- Rabbit (code "dumisen") ----
function drawRabbit(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 0.8, y + 1, 5.5, 4);
  g.fill({ color: 0x000000, alpha: 0.10 });
  // Body (white fluff)
  g.ellipse(x, y, 5.5, 4);
  g.fill({ color: 0xf8f4ee });
  g.ellipse(x, y, 5.5, 4);
  g.stroke({ color: 0xb8b0a0, width: 0.7 });
  // Grey back patch
  g.ellipse(x - 1, y, 3, 2.5);
  g.fill({ color: 0xb8a898, alpha: 0.7 });
  // Fluffy cotton tail
  g.circle(x - 6, y, 1.8);
  g.fill({ color: 0xffffff });
  g.circle(x - 6, y, 1.8);
  g.stroke({ color: 0xb8b0a0, width: 0.5 });
  // Head
  g.circle(x + 6, y, 3.3);
  g.fill({ color: 0xf8f4ee });
  g.circle(x + 6, y, 3.3);
  g.stroke({ color: 0xb8b0a0, width: 0.7 });
  // Long upright ears
  g.ellipse(x + 5.3, y - 5, 1.2, 3.8);
  g.fill({ color: 0xf8f4ee });
  g.ellipse(x + 5.3, y - 5, 1.2, 3.8);
  g.stroke({ color: 0xb8b0a0, width: 0.6 });
  g.ellipse(x + 6.7, y + 5, 1.2, 3.8);
  g.fill({ color: 0xf8f4ee });
  g.ellipse(x + 6.7, y + 5, 1.2, 3.8);
  g.stroke({ color: 0xb8b0a0, width: 0.6 });
  // Inner pink ears
  g.ellipse(x + 5.3, y - 5, 0.5, 2.5);
  g.fill({ color: 0xffaabb });
  g.ellipse(x + 6.7, y + 5, 0.5, 2.5);
  g.fill({ color: 0xffaabb });
  // Eyes
  g.circle(x + 7, y - 1.3, 0.8);
  g.fill({ color: 0x111111 });
  g.circle(x + 7, y + 1.3, 0.8);
  g.fill({ color: 0x111111 });
  // Pink nose
  g.moveTo(x + 8.8, y - 0.5); g.lineTo(x + 10, y); g.lineTo(x + 8.8, y + 0.5);
  g.closePath(); g.fill({ color: 0xffaabb });
}

// ---- Goldfish ----
function drawGoldfish(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 0.6, y + 1, 5, 3);
  g.fill({ color: 0x000000, alpha: 0.10 });
  // Body (orange, teardrop shape using overlapping ellipses)
  g.ellipse(x, y, 5, 3);
  g.fill({ color: 0xff8820 });
  g.ellipse(x, y, 5, 3);
  g.stroke({ color: 0xc04000, width: 0.7 });
  // Tail fins
  g.moveTo(x - 4, y); g.lineTo(x - 8, y - 3); g.lineTo(x - 7, y); g.lineTo(x - 8, y + 3);
  g.closePath();
  g.fill({ color: 0xff9020 });
  g.stroke({ color: 0xc04000, width: 0.6 });
  // Top fin
  g.moveTo(x - 1, y - 2.5); g.lineTo(x + 1, y - 5); g.lineTo(x + 2, y - 2.5);
  g.closePath();
  g.fill({ color: 0xff9020 });
  g.stroke({ color: 0xc04000, width: 0.5 });
  // Side fin
  g.ellipse(x - 1, y + 2.5, 1.5, 0.8);
  g.fill({ color: 0xffa040 });
  // Eye
  g.circle(x + 3, y - 0.5, 0.8);
  g.fill({ color: 0xffffff });
  g.circle(x + 3, y - 0.5, 0.5);
  g.fill({ color: 0x111111 });
  // Mouth
  g.circle(x + 5, y, 0.5);
  g.fill({ color: 0xc04000 });
}

// ---- Hamster ----
function drawHamster(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 0.8, y + 1, 5, 4);
  g.fill({ color: 0x000000, alpha: 0.10 });
  // Chubby body (warm brown)
  g.ellipse(x, y, 5, 4);
  g.fill({ color: 0xc89060 });
  g.ellipse(x, y, 5, 4);
  g.stroke({ color: 0x8a5a30, width: 0.7 });
  // Lighter belly
  g.ellipse(x, y, 3, 2);
  g.fill({ color: 0xf0d8b0, alpha: 0.8 });
  // Head (big cheeks)
  g.circle(x + 5, y, 3.2);
  g.fill({ color: 0xd8a070 });
  g.circle(x + 5, y, 3.2);
  g.stroke({ color: 0x8a5a30, width: 0.7 });
  // Full cheeks (two bulges)
  g.circle(x + 6, y - 2.2, 1.4);
  g.fill({ color: 0xe0b080 });
  g.circle(x + 6, y + 2.2, 1.4);
  g.fill({ color: 0xe0b080 });
  // Tiny round ears
  g.circle(x + 3.5, y - 2.8, 1);
  g.fill({ color: 0x8a5a30 });
  g.circle(x + 3.5, y + 2.8, 1);
  g.fill({ color: 0x8a5a30 });
  g.circle(x + 3.5, y - 2.8, 0.5);
  g.fill({ color: 0xffaabb });
  g.circle(x + 3.5, y + 2.8, 0.5);
  g.fill({ color: 0xffaabb });
  // Eyes
  g.circle(x + 6.5, y - 1, 0.7);
  g.fill({ color: 0x111111 });
  g.circle(x + 6.5, y + 1, 0.7);
  g.fill({ color: 0x111111 });
  // Pink nose
  g.circle(x + 8, y, 0.6);
  g.fill({ color: 0xff8888 });
}

// ---- Snake ----
function drawSnake(g: Graphics, x: number, y: number): void {
  // Drawn as thick curvy line (top-down squiggle)
  const thick = 3;
  // Path points
  const pts = [
    { x: x - 8, y: y + 2 },
    { x: x - 5, y: y - 2 },
    { x: x - 2, y: y + 2 },
    { x: x + 1, y: y - 2 },
    { x: x + 4, y: y + 1 },
  ];
  // Outline thick: draw a series of overlapping circles along the path
  for (const p of pts) {
    g.circle(p.x, p.y, thick);
    g.fill({ color: 0x5a8a30 });
    g.circle(p.x, p.y, thick);
    g.stroke({ color: 0x2a5010, width: 0.7 });
  }
  // Diamond pattern (darker spots)
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i];
    g.circle(p.x, p.y, 1);
    g.fill({ color: 0x2a5010, alpha: 0.7 });
  }
  // Head (slightly larger, rounded triangle)
  g.circle(x + 6, y + 0.5, 3);
  g.fill({ color: 0x6aa040 });
  g.circle(x + 6, y + 0.5, 3);
  g.stroke({ color: 0x2a5010, width: 0.7 });
  // Eyes
  g.circle(x + 7, y - 0.5, 0.6);
  g.fill({ color: 0xffcc00 });
  g.circle(x + 7, y + 1.5, 0.6);
  g.fill({ color: 0xffcc00 });
  g.circle(x + 7, y - 0.5, 0.3);
  g.fill({ color: 0x111111 });
  g.circle(x + 7, y + 1.5, 0.3);
  g.fill({ color: 0x111111 });
  // Forked tongue
  g.moveTo(x + 9, y + 0.5); g.lineTo(x + 11, y - 0.3);
  g.moveTo(x + 9, y + 0.5); g.lineTo(x + 11, y + 1.3);
  g.stroke({ color: 0xe04040, width: 0.8, cap: 'round' });
}

// ---- Frog ----
function drawFrog(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 0.6, y + 1, 5, 4);
  g.fill({ color: 0x000000, alpha: 0.10 });
  // Squat body (bright green)
  g.ellipse(x, y, 5, 4);
  g.fill({ color: 0x50b030 });
  g.ellipse(x, y, 5, 4);
  g.stroke({ color: 0x205810, width: 0.8 });
  // Pale belly stripe
  g.ellipse(x, y, 3.5, 1.8);
  g.fill({ color: 0xc8e890, alpha: 0.8 });
  // Bulging eyes (on top of head — two bumps)
  g.circle(x + 3, y - 3, 1.8);
  g.fill({ color: 0x70c040 });
  g.circle(x + 3, y - 3, 1.8);
  g.stroke({ color: 0x205810, width: 0.6 });
  g.circle(x + 3, y + 3, 1.8);
  g.fill({ color: 0x70c040 });
  g.circle(x + 3, y + 3, 1.8);
  g.stroke({ color: 0x205810, width: 0.6 });
  // Eye whites + pupils
  g.circle(x + 3.2, y - 3, 1);
  g.fill({ color: 0xffff60 });
  g.circle(x + 3.2, y + 3, 1);
  g.fill({ color: 0xffff60 });
  g.circle(x + 3.5, y - 3, 0.5);
  g.fill({ color: 0x111111 });
  g.circle(x + 3.5, y + 3, 0.5);
  g.fill({ color: 0x111111 });
  // Legs (splayed webbed feet)
  g.ellipse(x - 3, y - 4, 1.5, 1);
  g.fill({ color: 0x50b030 });
  g.ellipse(x - 3, y + 4, 1.5, 1);
  g.fill({ color: 0x50b030 });
  g.ellipse(x + 5, y - 4, 1.5, 1);
  g.fill({ color: 0x50b030 });
  g.ellipse(x + 5, y + 4, 1.5, 1);
  g.fill({ color: 0x50b030 });
  // Mouth line
  g.moveTo(x + 6, y - 0.5); g.lineTo(x + 7, y + 0.5);
  g.stroke({ color: 0x205810, width: 0.7 });
}

// ---- Mouse ----
function drawMouse(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 0.6, y + 1, 4.5, 3);
  g.fill({ color: 0x000000, alpha: 0.10 });
  // Small grey body
  g.ellipse(x, y, 4.5, 3);
  g.fill({ color: 0xb0b0b0 });
  g.ellipse(x, y, 4.5, 3);
  g.stroke({ color: 0x707070, width: 0.6 });
  // Pink thin tail
  g.moveTo(x - 4, y);
  g.quadraticCurveTo(x - 7, y + 3, x - 9, y - 1);
  g.stroke({ color: 0xffaabb, width: 1, cap: 'round' });
  // Head (pointed snout forward)
  g.circle(x + 4, y, 2.5);
  g.fill({ color: 0xbcbcbc });
  g.circle(x + 4, y, 2.5);
  g.stroke({ color: 0x707070, width: 0.6 });
  // Big round ears
  g.circle(x + 3, y - 3, 1.6);
  g.fill({ color: 0xb0b0b0 });
  g.circle(x + 3, y - 3, 1.6);
  g.stroke({ color: 0x707070, width: 0.5 });
  g.circle(x + 3, y + 3, 1.6);
  g.fill({ color: 0xb0b0b0 });
  g.circle(x + 3, y + 3, 1.6);
  g.stroke({ color: 0x707070, width: 0.5 });
  // Pink ear insides
  g.circle(x + 3, y - 3, 0.8);
  g.fill({ color: 0xff8899 });
  g.circle(x + 3, y + 3, 0.8);
  g.fill({ color: 0xff8899 });
  // Tiny eyes
  g.circle(x + 5, y - 1, 0.5);
  g.fill({ color: 0x111111 });
  g.circle(x + 5, y + 1, 0.5);
  g.fill({ color: 0x111111 });
  // Pink pointy nose
  g.circle(x + 6.5, y, 0.6);
  g.fill({ color: 0xff8899 });
  // Whiskers
  g.moveTo(x + 5.8, y - 0.3); g.lineTo(x + 9, y - 1);
  g.moveTo(x + 5.8, y + 0.3); g.lineTo(x + 9, y + 1);
  g.stroke({ color: 0x606060, width: 0.4 });
}

// ---- Shark ----
function drawShark(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 0.8, y + 1, 8, 3);
  g.fill({ color: 0x000000, alpha: 0.15 });
  // Torpedo body (steel-grey top-down view)
  g.ellipse(x, y, 8, 3);
  g.fill({ color: 0x607888 });
  g.ellipse(x, y, 8, 3);
  g.stroke({ color: 0x304050, width: 0.8 });
  // Pale underbelly stripe
  g.ellipse(x, y, 6, 1.2);
  g.fill({ color: 0xd8e0e8, alpha: 0.7 });
  // Dorsal fin (pointing up out of water — draw as prominent triangle on top)
  g.moveTo(x - 1, y - 2); g.lineTo(x + 1, y - 6); g.lineTo(x + 2, y - 2);
  g.closePath();
  g.fill({ color: 0x506878 });
  g.stroke({ color: 0x304050, width: 0.7 });
  // Pectoral fins (side)
  g.moveTo(x, y - 2.5); g.lineTo(x - 2, y - 6); g.lineTo(x + 2, y - 2.5);
  g.closePath();
  g.fill({ color: 0x506878 });
  g.stroke({ color: 0x304050, width: 0.5 });
  g.moveTo(x, y + 2.5); g.lineTo(x - 2, y + 6); g.lineTo(x + 2, y + 2.5);
  g.closePath();
  g.fill({ color: 0x506878 });
  g.stroke({ color: 0x304050, width: 0.5 });
  // Tail (vertical fork at rear)
  g.moveTo(x - 7, y); g.lineTo(x - 11, y - 3); g.lineTo(x - 9, y); g.lineTo(x - 11, y + 3);
  g.closePath();
  g.fill({ color: 0x506878 });
  g.stroke({ color: 0x304050, width: 0.7 });
  // Eye
  g.circle(x + 5, y - 1, 0.7);
  g.fill({ color: 0x111111 });
  // Gill slits
  for (let i = 0; i < 3; i++) {
    g.moveTo(x + 2 + i * 0.8, y - 1); g.lineTo(x + 2 + i * 0.8, y + 1);
    g.stroke({ color: 0x304050, width: 0.5 });
  }
  // Toothy mouth
  g.moveTo(x + 6, y); g.lineTo(x + 8.5, y - 0.5); g.lineTo(x + 9, y);
  g.lineTo(x + 8.5, y + 0.5); g.closePath();
  g.fill({ color: 0xffffff });
  g.stroke({ color: 0x304050, width: 0.5 });
}

// ---- Rat ----
function drawRat(g: Graphics, x: number, y: number): void {
  g.ellipse(x + 0.8, y + 1, 5.5, 3.5);
  g.fill({ color: 0x000000, alpha: 0.12 });
  // Larger, darker body than mouse
  g.ellipse(x, y, 5.5, 3.5);
  g.fill({ color: 0x807060 });
  g.ellipse(x, y, 5.5, 3.5);
  g.stroke({ color: 0x403828, width: 0.7 });
  // Long straight pink tail
  g.moveTo(x - 5, y);
  g.lineTo(x - 12, y + 1);
  g.stroke({ color: 0xe8b0a0, width: 1.2, cap: 'round' });
  // Head (pointed snout)
  g.circle(x + 5, y, 2.8);
  g.fill({ color: 0x907868 });
  g.circle(x + 5, y, 2.8);
  g.stroke({ color: 0x403828, width: 0.7 });
  // Smaller rounded ears
  g.circle(x + 4, y - 3, 1.3);
  g.fill({ color: 0x807060 });
  g.circle(x + 4, y - 3, 1.3);
  g.stroke({ color: 0x403828, width: 0.5 });
  g.circle(x + 4, y + 3, 1.3);
  g.fill({ color: 0x807060 });
  g.circle(x + 4, y + 3, 1.3);
  g.stroke({ color: 0x403828, width: 0.5 });
  // Pink ear insides
  g.circle(x + 4, y - 3, 0.6);
  g.fill({ color: 0xd08888 });
  g.circle(x + 4, y + 3, 0.6);
  g.fill({ color: 0xd08888 });
  // Beady eyes
  g.circle(x + 6, y - 1, 0.7);
  g.fill({ color: 0x111111 });
  g.circle(x + 6, y + 1, 0.7);
  g.fill({ color: 0x111111 });
  // Pointy pink snout tip
  g.moveTo(x + 7, y - 0.6); g.lineTo(x + 9, y); g.lineTo(x + 7, y + 0.6);
  g.closePath(); g.fill({ color: 0xd08888 });
  // Long whiskers
  g.moveTo(x + 7, y - 0.3); g.lineTo(x + 11, y - 1.5);
  g.moveTo(x + 7, y + 0.3); g.lineTo(x + 11, y + 1.5);
  g.stroke({ color: 0x303030, width: 0.4 });
}
